import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  Bloodtype as BloodtypeIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import api from "../api/api";

const bloodGroupOptions = [
  "A+",
  "A-",
  "A1+",
  "A1-",
  "A2+",
  "A2-",
  "B+",
  "B-",
  "AB+",
  "AB-",
  "A1B+",
  "A1B-",
  "A2B+",
  "A2B-",
  "O+",
  "O-",
  "Bombay Blood Group",
  "Rh-null",
];

function normalizeApiResponse(payload) {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.donors)) {
    return payload.donors;
  }

  if (Array.isArray(payload?.users)) {
    return payload.users;
  }

  if (Array.isArray(payload?.result)) {
    return payload.result;
  }

  return [];
}

function toDonorRecord(item, index) {
  const firstName = item?.FirstName || item?.firstName || "";
  const lastName = item?.LastName || item?.lastName || "";
  const alumniName = item?.alumniName || `${firstName} ${lastName}`.trim();
  const bloodGroup = item?.BloodGroup || item?.bloodGroup || "";
  const interested =
    item?.interestedToDonate ??
    item?.InterestedToDonate ??
    item?.IntrestToDonateBlood ??
    item?.interestToDonateBlood ??
    0;
  const city = item?.City || item?.city || "";
  const state = item?.State || item?.state || "";
  const country = item?.Country || item?.country || "";
  const combinedLocation =
    item?.location || [city, state, country].filter(Boolean).join(", ");

  return {
    id: item?.id || item?.Id || item?.Email || item?.email || index,
    alumniName,
    bloodGroup,
    mobileNumber: item?.MobileNumber || item?.mobileNumber || "—",
    email: item?.Email || item?.email || "",
    department: item?.Department || item?.department || "",
    yearOfPassedOut: item?.YearOfPassedOut || item?.yearOfPassedOut || "",
    currentJob: item?.CurrentJob || item?.currentJob || "",
    companyName: item?.CompanyName || item?.companyName || "",
    location: combinedLocation,
    interestedToDonate:
      typeof interested === "boolean" ? interested : Number(interested) === 1,
  };
}

function BloodDonar() {
  const userState = useSelector((state) => state.user);
  const currentUser = userState?.user || {};
  const currentUserToken =
    currentUser.token || currentUser.accessToken || currentUser.jwtToken || "";
  const [donors, setDonors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [bloodGroupFilter, setBloodGroupFilter] = useState("all");

  useEffect(() => {
    let active = true;

    const loadDonors = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await api.getBloodDonors(currentUserToken);
        const payload = response?.data ?? response;
        const users = normalizeApiResponse(payload)
          .map(toDonorRecord)
          .filter(
            (item) =>
              item.interestedToDonate && item.alumniName && item.bloodGroup,
          );

        if (active) {
          setDonors(users);
        }
      } catch (err) {
        if (active) {
          setError(
            err?.response?.data?.message ||
              err?.message ||
              "Unable to load blood donor details.",
          );
          setDonors([]);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadDonors();

    return () => {
      active = false;
    };
  }, [currentUserToken]);

  const filteredDonors = useMemo(() => {
    const searchTerm = search.trim().toLowerCase();

    return donors.filter((item) => {
      const matchesBloodGroup =
        bloodGroupFilter === "all" || item.bloodGroup === bloodGroupFilter;

      const matchesSearch =
        !searchTerm ||
        item.alumniName.toLowerCase().includes(searchTerm) ||
        item.location.toLowerCase().includes(searchTerm) ||
        item.mobileNumber.toLowerCase().includes(searchTerm) ||
        item.department.toLowerCase().includes(searchTerm);

      return matchesBloodGroup && matchesSearch;
    });
  }, [bloodGroupFilter, donors, search]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #f8fafc 0%, #eef5ff 100%)",
        py: { xs: 4, md: 6 },
      }}
    >
      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 4,
              background:
                "linear-gradient(135deg, rgba(220,38,38,0.98) 0%, rgba(153,27,27,0.98) 100%)",
              color: "white",
              boxShadow: "0 18px 45px rgba(127, 29, 29, 0.22)",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              justifyContent="space-between"
            >
              <Box>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{ mb: 1.5 }}
                >
                  <BloodtypeIcon />
                  <Typography variant="h4" fontWeight={700}>
                    Blood Donor Directory
                  </Typography>
                </Stack>
                <Typography
                  color="rgba(255,255,255,0.88)"
                  sx={{ maxWidth: 720 }}
                >
                  Find alumni donors by blood group and contact them directly in
                  urgent situations.
                </Typography>
              </Box>
              <Chip
                label={`${filteredDonors.length} donor${filteredDonors.length === 1 ? "" : "s"}`}
                sx={{
                  alignSelf: { xs: "flex-start", md: "center" },
                  bgcolor: "rgba(255,255,255,0.18)",
                  color: "white",
                  fontWeight: 700,
                }}
              />
            </Stack>
          </Paper>

          <Paper elevation={0} sx={{ p: 3, borderRadius: 4 }}>
            <Stack spacing={3}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", md: "center" }}
              >
                <TextField
                  fullWidth
                  placeholder="Search by alumni name, location, department or phone"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "text.secondary" }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl sx={{ minWidth: { xs: "100%", md: 220 } }}>
                  <InputLabel id="blood-group-filter-label">
                    Blood group
                  </InputLabel>
                  <Select
                    labelId="blood-group-filter-label"
                    value={bloodGroupFilter}
                    label="Blood group"
                    onChange={(event) =>
                      setBloodGroupFilter(event.target.value)
                    }
                  >
                    <MenuItem value="all">All blood groups</MenuItem>
                    {bloodGroupOptions.map((group) => (
                      <MenuItem key={group} value={group}>
                        {group}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              {error ? <Alert severity="error">{error}</Alert> : null}

              {loading ? (
                <Stack alignItems="center" py={8} spacing={2}>
                  <CircularProgress />
                  <Typography color="text.secondary">
                    Loading donor details...
                  </Typography>
                </Stack>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead sx={{ bgcolor: "#f8fafc" }}>
                      <TableRow>
                        <TableCell>Alumni name</TableCell>
                        <TableCell>Blood group</TableCell>
                        {/* <TableCell>Contact number</TableCell> */}
                        <TableCell>Location</TableCell>
                        <TableCell>Department</TableCell>
                        <TableCell>Passed out</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredDonors.length ? (
                        filteredDonors.map((item) => (
                          <TableRow key={item.id} hover>
                            <TableCell>
                              <Stack spacing={0.5}>
                                <Typography fontWeight={600}>
                                  {item.alumniName}
                                </Typography>
                                {item.email ? (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {item.email}
                                  </Typography>
                                ) : null}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={item.bloodGroup}
                                color="error"
                                variant="outlined"
                                size="small"
                              />
                            </TableCell>
                            {/* <TableCell>{item.mobileNumber}</TableCell> */}
                            <TableCell>{item.location || "—"}</TableCell>
                            <TableCell>{item.department || "—"}</TableCell>
                            <TableCell>{item.yearOfPassedOut || "—"}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            sx={{ py: 6, textAlign: "center" }}
                          >
                            <Typography fontWeight={600} sx={{ mb: 0.5 }}>
                              No donors found
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Adjust the blood group filter or search terms.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Stack>
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
}

export default BloodDonar;
